import { useTranslation } from "react-i18next";
import type { CrmDashboardHeatmapSubpipelineMeta } from "@shared/api";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { DashboardQueryFilters } from "@/hooks/useDashboardData";

type Labeled = { label: string; labelHu: string; labelSk: string };

function pickLocalizedLabel(locale: string, o: Labeled): string {
  if (locale.startsWith("hu")) return o.labelHu || o.label;
  if (locale.startsWith("sk")) return o.labelSk || o.label;
  return o.label;
}

export function DashboardFiltersBar({
  filters,
  onChange,
  subpipelines,
  className,
}: {
  filters: DashboardQueryFilters;
  onChange: (next: DashboardQueryFilters) => void;
  subpipelines: CrmDashboardHeatmapSubpipelineMeta[];
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const set = (patch: Partial<DashboardQueryFilters>) => onChange({ ...filters, ...patch });

  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 shadow-sm flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end",
        className,
      )}
    >
      <div className="flex flex-col gap-1.5 min-w-[200px] flex-1 lg:max-w-xs">
        <Label htmlFor="dash-filter-pipeline" className="text-xs text-muted-foreground">
          {t("dashboard.filterSubpipeline")}
        </Label>
        <Select
          value={filters.pipelineId || "__all__"}
          onValueChange={(v) => set({ pipelineId: v === "__all__" ? "" : v })}
        >
          <SelectTrigger id="dash-filter-pipeline" className="h-9">
            <SelectValue placeholder={t("dashboard.filterAllPipelines")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t("dashboard.filterAllPipelines")}</SelectItem>
            {subpipelines.map((p) => (
              <SelectItem key={p.pipelineId} value={p.pipelineId}>
                {pickLocalizedLabel(locale, p)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5 min-w-[140px]">
        <Label htmlFor="dash-filter-from" className="text-xs text-muted-foreground">
          {t("dashboard.filterDateFrom")}
        </Label>
        <Input
          id="dash-filter-from"
          type="date"
          className="h-9"
          value={filters.dateFrom}
          onChange={(e) => set({ dateFrom: e.target.value })}
        />
      </div>

      <div className="flex flex-col gap-1.5 min-w-[140px]">
        <Label htmlFor="dash-filter-to" className="text-xs text-muted-foreground">
          {t("dashboard.filterDateTo")}
        </Label>
        <Input
          id="dash-filter-to"
          type="date"
          className="h-9"
          value={filters.dateTo}
          onChange={(e) => set({ dateTo: e.target.value })}
        />
      </div>

      <p className="text-xs text-muted-foreground lg:flex-1 lg:min-w-[200px] lg:pb-1">{t("dashboard.filterHint")}</p>
    </div>
  );
}
